import { ROUTE_CONFIG } from "@/routes/config";

import {
	DashboardIssue,
	DashboardIssueCreditsLowPayload,
	DashboardIssueCrmSyncPayload,
	DashboardIssueHubspotNoteOwnerMissingPayload,
	DashboardIssueListNoResultsPayload,
	DashboardIssueOutreachNotActivatedPayload,
	DashboardIssueType,
} from "../schema/dashboard";

import { campaignPausedIssueDisplay } from "./campaignPausedIssueDisplay";
import { crmReconnectIssueDisplay } from "./crmReconnectIssueDisplay";
import { dynamicsOwnerPrivilegeIssueDisplay } from "./dynamicsOwnerPrivilegeIssueDisplay";
import { zohoSearchFieldIssueDisplay } from "./zohoSearchFieldIssueDisplay";

export interface IssueDisplayProps {
	title: string;
	description: string;
	primaryActionLabel: string;
	primaryActionTo: string;
	/**
	 * When set, the primary control performs an inline action instead of navigating to
	 * `primaryActionTo`. "activate-outreach" fires the list campaign-activation mutation
	 * (handled in IssuesBox), matching the campaign toggle on the list overview.
	 */
	primaryAction?: "activate-outreach";
}

const asListNoResultsPayload = (
	issue: DashboardIssue
): DashboardIssueListNoResultsPayload | null => {
	const name = issue.payload?.listName;

	if (typeof name !== "string") {
		return null;
	}

	return { listName: name };
};

const asOutreachNotActivatedPayload = (
	issue: DashboardIssue
): DashboardIssueOutreachNotActivatedPayload | null => {
	const listName = issue.payload?.listName;
	const assignedCount = issue.payload?.assignedCount;

	if (typeof listName !== "string" || typeof assignedCount !== "number") {
		return null;
	}

	return { listName, assignedCount };
};

const asCrmSyncPayload = (issue: DashboardIssue): DashboardIssueCrmSyncPayload | null => {
	const total = issue.payload?.total;

	if (typeof total !== "number") {
		return null;
	}

	const counts = issue.payload?.counts;
	const safeCounts: DashboardIssueCrmSyncPayload["counts"] = {
		prospects: 0,
		orgs: 0,
		leads: 0,
		deals: 0,
	};

	if (counts && typeof counts === "object") {
		const c = counts as Record<string, unknown>;

		if (typeof c.prospects === "number") {
			safeCounts.prospects = c.prospects;
		}

		if (typeof c.orgs === "number") {
			safeCounts.orgs = c.orgs;
		}

		if (typeof c.leads === "number") {
			safeCounts.leads = c.leads;
		}

		if (typeof c.deals === "number") {
			safeCounts.deals = c.deals;
		}
	}

	return { total, counts: safeCounts };
};

const asHubspotNoteOwnerMissingPayload = (
	issue: DashboardIssue
): DashboardIssueHubspotNoteOwnerMissingPayload | null => {
	const conversations = issue.payload?.conversations;
	const senders = issue.payload?.senders;

	if (typeof conversations !== "number" || !Array.isArray(senders)) {
		return null;
	}

	return {
		conversations,
		senders: senders
			.filter(
				(sender): sender is { id: number; name: string } =>
					typeof sender === "object" &&
					sender !== null &&
					typeof (sender as { name?: unknown }).name === "string"
			)
			.map((sender) => ({ id: Number(sender.id), name: sender.name })),
	};
};

const asCreditsLowPayload = (issue: DashboardIssue): DashboardIssueCreditsLowPayload | null => {
	const p = issue.payload ?? {};

	if (
		typeof p.remaining !== "number" ||
		typeof p.cycleCredits !== "number" ||
		typeof p.threshold !== "number" ||
		typeof p.perMonthOrYear !== "string" ||
		typeof p.nextCycle !== "string"
	) {
		return null;
	}

	return {
		remaining: p.remaining,
		cycleCredits: p.cycleCredits,
		threshold: p.threshold,
		perMonthOrYear: p.perMonthOrYear,
		nextCycle: p.nextCycle,
	};
};

export const issueToDisplay = (issue: DashboardIssue): IssueDisplayProps => {
	switch (issue.type) {
		case DashboardIssueType.LINKEDIN_DISCONNECTED:
			return {
				title: "Your LinkedIn is disconnected",
				description: "Lists and message scheduling are paused until you reconnect.",
				primaryActionLabel: "Reconnect",
				primaryActionTo: `${ROUTE_CONFIG.ACCOUNT}?reconnect=linkedin`,
			};
		case DashboardIssueType.LINKEDIN_SN_DISCONNECTED:
			return {
				title: "Sales Navigator is disconnected",
				description: "Sales Navigator features are paused until you reconnect.",
				primaryActionLabel: "Reconnect",
				primaryActionTo: `${ROUTE_CONFIG.ACCOUNT}?reconnect=linkedin`,
			};
		case DashboardIssueType.EMAIL_LOGGED_OUT: {
			const email = typeof issue.payload?.email === "string" ? issue.payload.email : null;

			return {
				title: email ? `${email} is disconnected` : "An email inbox is disconnected",
				description: "Sending and reply tracking are paused for this inbox until you reconnect.",
				primaryActionLabel: "Reconnect",
				primaryActionTo: `${ROUTE_CONFIG.ACCOUNT}?tab=CONNECTED_ACCOUNTS`,
			};
		}

		case DashboardIssueType.CRM_SYNC_FAILED: {
			const payload = asCrmSyncPayload(issue);
			const total = payload?.total ?? 0;

			// The API counts only rows that failed in the last 7 days (PREC-4301).
			return {
				title: `${total} CRM record${total === 1 ? "" : "s"} failed to sync in the last 7 days`,
				description:
					"Open your CRM under Integrations and check its Sync Logs tab to see what the CRM answered.",
				primaryActionLabel: "View errors",
				primaryActionTo: ROUTE_CONFIG.INTEGRATIONS,
			};
		}

		case DashboardIssueType.LIST_NO_RESULTS_ONGOING: {
			const payload = asListNoResultsPayload(issue);
			const name = payload?.listName ?? "List";

			return {
				title: `"${name}" had no results in the last run`,
				description: "Filters may be too narrow. Open the list to review them.",
				primaryActionLabel: "Open list",
				primaryActionTo: issue.entityId
					? `${ROUTE_CONFIG.LISTS_DETAIL}/${issue.entityId}`
					: ROUTE_CONFIG.LISTS,
			};
		}

		case DashboardIssueType.LIST_NO_RESULTS_ONCE: {
			const payload = asListNoResultsPayload(issue);
			const name = payload?.listName ?? "List";

			return {
				title: `"${name}" finished with no results`,
				description: "The list ran once and returned no leads. Review the filters or sources.",
				primaryActionLabel: "Open list",
				primaryActionTo: issue.entityId
					? `${ROUTE_CONFIG.LISTS_DETAIL}/${issue.entityId}`
					: ROUTE_CONFIG.LISTS,
			};
		}

		case DashboardIssueType.LIST_OUTREACH_NOT_ACTIVATED: {
			const payload = asOutreachNotActivatedPayload(issue);
			const name = payload?.listName ?? "List";
			const count = payload?.assignedCount ?? 0;

			return {
				title: `"${name}" has ${count} assigned lead${count === 1 ? "" : "s"} but outreach isn't active`,
				description: "These leads won't be scheduled until you activate outreach for the list.",
				primaryActionLabel: "Activate",
				// Activate outreach inline (like the campaign toggle on the list overview) when we
				// know which list to activate; otherwise fall back to the lists overview.
				primaryAction: issue.entityId ? "activate-outreach" : undefined,
				primaryActionTo: issue.entityId
					? `${ROUTE_CONFIG.LISTS_DETAIL}/${issue.entityId}`
					: ROUTE_CONFIG.LISTS,
			};
		}

		case DashboardIssueType.COMPETITORS_NEED_REVIEW: {
			const count = typeof issue.payload?.count === "number" ? issue.payload.count : 0;
			const listCount = typeof issue.payload?.listCount === "number" ? issue.payload.listCount : 0;

			return {
				// "x competitors identified on y lists" (PREC-3594) — the list count is what makes
				// the hint concrete; without it the number of companies means nothing to the user.
				title: `${count} competitor${count === 1 ? "" : "s"} identified${
					listCount > 0 ? ` on ${listCount} list${listCount === 1 ? "" : "s"}` : ""
				}`,
				// The point of the hint: competitors are excluded everywhere, for good,
				// until someone reviews the list (PREC-3373).
				description:
					"They are excluded from every list and search across your account until you review them.",
				primaryActionLabel: "Review them",
				primaryActionTo: ROUTE_CONFIG.DO_NOT_CONTACT_COMPETITORS,
			};
		}

		case DashboardIssueType.HUBSPOT_NOTE_OWNER_MISSING: {
			// PREC-3984: a HubSpot note without an owner is invisible in HubSpot, so the notes wait
			// until the sender is mapped to an owner — naming whom to map is the whole point.
			const payload = asHubspotNoteOwnerMissingPayload(issue);
			const count = payload?.conversations;
			const senders = payload?.senders ?? [];
			const who =
				senders.length === 1
					? `${senders[0].name} has no HubSpot owner`
					: senders.length > 1
						? `${senders.length} users have no HubSpot owner`
						: "a sender has no HubSpot owner";
			const what =
				count === undefined
					? "HubSpot notes are waiting"
					: `${count} HubSpot note${count === 1 ? " is" : "s are"} waiting`;

			return {
				title: `${what}: ${who}`,
				description:
					"HubSpot only shows a note that has an owner. Map these users to their HubSpot owner and the waiting notes sync within 30 minutes.",
				primaryActionLabel: "Map users",
				primaryActionTo: ROUTE_CONFIG.INTEGRATIONS_HUBSPOT,
			};
		}

		case DashboardIssueType.CAMPAIGN_PAUSED_UNSENDABLE:
			return campaignPausedIssueDisplay(issue);

		case DashboardIssueType.DYNAMICS_OWNER_PRIVILEGE_MISSING:
			return dynamicsOwnerPrivilegeIssueDisplay(issue);

		case DashboardIssueType.CRM_RECONNECT_REQUIRED:
			return crmReconnectIssueDisplay(issue);

		case DashboardIssueType.ZOHO_LINKEDIN_FIELD_NOT_SEARCHABLE:
			return zohoSearchFieldIssueDisplay(issue);

		case DashboardIssueType.CREDITS_LOW: {
			const payload = asCreditsLowPayload(issue);

			if (!payload) {
				return {
					title: "You're running low on credits",
					description: "Upgrade your plan to keep lists running.",
					primaryActionLabel: "Upgrade plan",
					primaryActionTo: `${ROUTE_CONFIG.COMPANY}?tab=BILLING`,
				};
			}

			return {
				title: `${payload.remaining} credits left of ${payload.cycleCredits}`,
				description: `Threshold reached at ${payload.threshold}. Renews on ${new Date(payload.nextCycle).toLocaleDateString()}.`,
				primaryActionLabel: "Upgrade plan",
				primaryActionTo: `${ROUTE_CONFIG.COMPANY}?tab=BILLING`,
			};
		}
	}
};
